import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ dragListeners: listeners, isDragging })}
    </div>
  );
}

/**
 * Reusable drag-and-drop sortable list.
 *
 * @param {Array} items - Array of items to render.
 * @param {Function} keyExtractor - Returns a unique string key for each item.
 * @param {Function} onReorder - Called with the reordered array after a drag.
 * @param {Function} renderItem - Render function: (item, index, { dragListeners, isDragging }) => JSX.
 */
export default function DraggableList({ items, keyExtractor, onReorder, renderItem }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => keyExtractor(item) === active.id);
    const newIndex = items.findIndex((item) => keyExtractor(item) === over.id);
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  const ids = items.map(keyExtractor);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {items.map((item, index) => (
          <SortableItem key={keyExtractor(item)} id={keyExtractor(item)}>
            {({ dragListeners, isDragging }) => renderItem(item, index, { dragListeners, isDragging })}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
}
